/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';

import { FileInput } from '../../../src/app/@types';
import { EStaticFileType, StaticUploadRequest } from '../../../src/app/@shared-types/misc/file';
import { SalesAPIService } from '../../../shared/api/sales-api.service';
import { FileService } from '../../../shared/storage/file.service';
import { DeepPartial } from '../../../src/app/@shared-types';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { BatteryHealth } from '../../../src/app/@shared-types/vehicle/vehicle';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';

interface BatteryHealthScope extends IScope {
  sending: boolean,
  exitForm: () => void,
  exit: (data: VehicleFormComponentExitFunction) => void,
  formTransmission: () => void,
  deleteCertificate: () => void,
  vehicle: AutoboardVehicle,
  setWarrantyYears: (years: number) => void,
  batteryData: {
    capacity?: number | null,
    warrantyYears?: number | null,
    warrantyMileage?: number | null,
    certificate?: FileInput,
    currentCertificateUrl?: string,
  },
}

/** Empty inputs are saved as `null` to remove existing values */
const toNumber = (value: number | null | undefined, decimals: number): number | null => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  const factor = 10 ** decimals;
  return Math.round(Number(value) * factor) / factor;
};

/* @ngInject */
export default function BatteryHealthFormController(
  $scope: BatteryHealthScope,
  fileService: FileService,
  salesApiService: SalesAPIService,
  toastService: ToastService,
): void {
  $scope.sending = false;

  const batteryHealth = $scope.vehicle.vehicleData.batteryHealth || {};
  $scope.batteryData = {
    capacity: batteryHealth.capacity,
    warrantyYears: batteryHealth.warrantyYears,
    warrantyMileage: batteryHealth.warrantyMileage,
  };

  /** Quick-set the warranty duration */
  $scope.setWarrantyYears = (years: number) => {
    $scope.batteryData.warrantyYears = years;
  };

  if (batteryHealth.certificateStaticId) {
    $scope.batteryData.currentCertificateUrl = fileService
      .getStaticUrl(batteryHealth.certificateStaticId);
  }

  $scope.exitForm = () => $scope.exit({ removeLock: true });

  const updateBatteryHealth = async (update: BatteryHealth): Promise<void> => {
    const changedVals: DeepPartial<AutoboardVehicle> = {
      _id: $scope.vehicle._id,
      vehicleData: {
        batteryHealth: update,
      },
    };

    await salesApiService.putVehicle(changedVals);
  };

  $scope.deleteCertificate = async () => {
    const staticId = batteryHealth.certificateStaticId;
    if (!staticId) return;

    $scope.sending = true;

    await updateBatteryHealth({ certificateStaticId: '' });
    await fileService.deleteStatic(staticId);

    $scope.exit({});
    toastService.toast(ToastType.SUCCESS, 'Batteriezertifikat wurde gelöscht.');
  };

  $scope.formTransmission = async () => {
    $scope.sending = true;

    const update: BatteryHealth = {
      capacity: toNumber($scope.batteryData.capacity, 1),
      warrantyYears: toNumber($scope.batteryData.warrantyYears, 0),
      warrantyMileage: toNumber($scope.batteryData.warrantyMileage, 0),
    };

    const { certificate } = $scope.batteryData;
    if (certificate?.file) {
      const uploadReq: StaticUploadRequest = {
        identifier: String($scope.vehicle._id),
        type: EStaticFileType.BATTERY_CERTIFICATE,
      };

      update.certificateStaticId = await fileService
        .apiUploadStatic(certificate.file, uploadReq, certificate.progressBar);
    }

    await updateBatteryHealth(update);

    /** A replaced certificate is no longer needed */
    const previousStaticId = batteryHealth.certificateStaticId;
    if (update.certificateStaticId && previousStaticId) {
      await fileService.deleteStatic(previousStaticId);
    }

    $scope.exit({});
  };
}
